import { listInuviFiles } from '../../api/fileStorage';
import { useEffect, useState, useRef, useCallback } from 'react';
import './InuviFiles.scss';
import Modal from '../../components/BaseComponents/Modal';
import Button from '../../components/BaseComponents/Button';
import TextInput from '../../components/BaseComponents/TextInput';
import Loader from '../../components/BaseComponents/Loader';
import { parseHL7 } from '../../utils/hl7Parser';
import { toast } from 'react-toastify';
import { getMembersListByUniqueId, assignInuviFileToMember, unassignFileFromMember, listMemberInuviFiles } from '../../api/members';
import { ListMembersResponseElement } from '../../types/apiGeneratedTypes';
import downloadIcon from '../../assets/icons/download.svg';
import viewIcon from '../../assets/icons/eye.svg';

const InuviFiles = () => {
  const [files, setFiles] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [loading, setLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState<any>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [parsedHL7, setParsedHL7] = useState<any>(null);
  const [isLoadingContent, setIsLoadingContent] = useState(false);
  const [showNotesModal, setShowNotesModal] = useState(false);
  const [selectedNotes, setSelectedNotes] = useState<string[]>([]);
  const [fileMemberNames, setFileMemberNames] = useState<{[key: string]: string}>({});
  const [selectedFileMemberData, setSelectedFileMemberData] = useState<ListMembersResponseElement | null>(null);
  const [selectedFileAssignmentStatus, setSelectedFileAssignmentStatus] = useState<{ isAssigned: boolean; memberId: string | null; memberName: string | null } | null>(null);
  const [isLoadingAssignment, setIsLoadingAssignment] = useState(false);
  const [manualMemberId, setManualMemberId] = useState('');
  const [isLookingUpMember, setIsLookingUpMember] = useState(false);
  
  // Pagination state
  const [hasMore, setHasMore] = useState<boolean>(true);
  const [isLoadingMore, setIsLoadingMore] = useState<boolean>(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Load more files function
  const loadMoreFiles = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;
    
    setIsLoadingMore(true);
    try {
      const response = await listInuviFiles(10, files.length);
      
      if (response.files && response.files.length > 0) {
        const sortedNewFiles = [...response.files].sort((a, b) => {
          const dateA = new Date(a.last_modified).getTime();
          const dateB = new Date(b.last_modified).getTime();
          return dateB - dateA;
        });
        
        setFiles(prev => [...prev, ...sortedNewFiles]);
        setHasMore(response.files.length >= 10);
        
        // Parse HL7 content for new files to extract member names
        const memberNames: {[key: string]: string} = {};
        
        for (const file of sortedNewFiles) {
          try {
            const response = await fetch(file.url, {
              method: 'GET',
              headers: {
                'Accept': 'text/plain',
              },
              mode: 'cors'
            });
            
            if (response.ok) {
              const content = await response.text();
              const parsed = parseHL7(content);
              memberNames[file.key] = parsed.memberInfo.name;
            }
          } catch (error) {
            console.error(`Error parsing file ${file.key}:`, error);
            memberNames[file.key] = 'Error parsing file';
          }
        }
        
        setFileMemberNames(prev => ({ ...prev, ...memberNames }));
      } else {
        setHasMore(false);
      }
    } catch (error) {
      console.error('Error loading more files:', error);
      toast.error('Failed to load more files');
    } finally {
      setIsLoadingMore(false);
    }
  }, [files.length, isLoadingMore, hasMore]);

  // Initial load
  useEffect(() => {
    const fetchInitialFiles = async () => {
      setLoading(true);
      try {
        const response = await listInuviFiles(10, 0);
        const sortedFiles = [...response.files].sort((a, b) => {
          const dateA = new Date(a.last_modified).getTime();
          const dateB = new Date(b.last_modified).getTime();
          return dateB - dateA;
        });
        setFiles(sortedFiles);
        setTotalCount(response.total_count);
        setHasMore(response.files.length >= 10);
        
        // Parse HL7 content for each file to extract member names only
        const memberNames: {[key: string]: string} = {};
        
        for (const file of sortedFiles) {
          try {
            const response = await fetch(file.url, {
              method: 'GET',
              headers: {
                'Accept': 'text/plain',
              },
              mode: 'cors'
            });
            
            if (response.ok) {
              const content = await response.text();
              const parsed = parseHL7(content);
              memberNames[file.key] = parsed.memberInfo.name;
            }
          } catch (error) {
            console.error(`Error parsing file ${file.key}:`, error);
            memberNames[file.key] = 'Error parsing file';
          }
        }
        
        setFileMemberNames(memberNames);
      } catch (error) {
        console.error('Error loading files:', error);
        toast.error('Failed to load files');
      } finally {
        setLoading(false);
      }
    };

    fetchInitialFiles();
  }, []);

  // Scroll handling for infinite scroll
  const handleScroll = useCallback(() => {
    if (scrollTimeoutRef.current) {
      clearTimeout(scrollTimeoutRef.current);
    }

    scrollTimeoutRef.current = setTimeout(() => {
      if (!scrollContainerRef.current || isLoadingMore || !hasMore) return;
      
      const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
      const threshold = 100;
      
      if (scrollTop + clientHeight >= scrollHeight - threshold) {
        loadMoreFiles();
      }
    }, 150);
  }, [isLoadingMore, hasMore, loadMoreFiles]);

  // Add scroll event listener
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (scrollContainer) {
      scrollContainer.addEventListener('scroll', handleScroll, { passive: true });
    }
    
    return () => {
      if (scrollContainer) {
        scrollContainer.removeEventListener('scroll', handleScroll);
      }
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [handleScroll]);

  const lookupMemberByUniqueId = async (
    uniqueId: string
  ): Promise<ListMembersResponseElement | null> => {
    const trimmedId = uniqueId.trim();
    if (!trimmedId) {
      return null;
    }

    const memberResponse = await getMembersListByUniqueId(trimmedId);
    return (
      memberResponse.members.find(
        (member) => member.unique_id === trimmedId
      ) || null
    );
  };

  const applyFoundMember = async (
    file: any,
    foundMember: ListMembersResponseElement
  ) => {
    setSelectedFileMemberData(foundMember);

    try {
      const memberFiles = await listMemberInuviFiles(foundMember.id);
      const isAssigned = memberFiles.files.some(
        (memberFile: any) => memberFile.file_key === file.key
      );
      setSelectedFileAssignmentStatus({
        isAssigned,
        memberId: foundMember.id,
        memberName: `${foundMember.first_name} ${foundMember.last_name}`,
      });
    } catch (error) {
      console.error(`Error checking assignment for file ${file.key}:`, error);
      setSelectedFileAssignmentStatus({
        isAssigned: false,
        memberId: foundMember.id,
        memberName: `${foundMember.first_name} ${foundMember.last_name}`,
      });
    }
  };

  const handleViewClick = async (file: any) => {
    setSelectedFile(file);
    setShowViewModal(true);
    setIsLoadingContent(true);
    setSelectedFileMemberData(null);
    setSelectedFileAssignmentStatus(null);
    setManualMemberId('');
    
    try {
      const response = await fetch(file.url, {
        method: 'GET',
        headers: {
          'Accept': 'text/plain',
        },
        mode: 'cors'
      });
      
      if (!response.ok) {
        throw new Error(`Failed to fetch file: ${response.statusText}`);
      }
      
      const content = await response.text();
      const parsed = parseHL7(content);
      setParsedHL7(parsed);
      
      // Try to find member by ID from HL7 file
      if (parsed.memberInfo.id) {
        try {
          const foundMember = await lookupMemberByUniqueId(parsed.memberInfo.id);

          if (foundMember) {
            await applyFoundMember(file, foundMember);
          } else {
            setSelectedFileMemberData(null);
            setSelectedFileAssignmentStatus({
              isAssigned: false,
              memberId: parsed.memberInfo.id,
              memberName: null,
            });
            setManualMemberId(parsed.memberInfo.id);
          }
        } catch (error) {
          console.error(`Error fetching member ${parsed.memberInfo.id}:`, error);
          setSelectedFileMemberData(null);
          setSelectedFileAssignmentStatus({
            isAssigned: false,
            memberId: parsed.memberInfo.id,
            memberName: null,
          });
          setManualMemberId(parsed.memberInfo.id);
        }
      } else {
        setSelectedFileMemberData(null);
        setSelectedFileAssignmentStatus({
          isAssigned: false,
          memberId: null,
          memberName: null
        });
      }
    } catch (error) {
      console.error('Error loading file content:', error);
      toast.error('Failed to load file content. Please check if CORS is properly configured on the S3 bucket.');
      setShowViewModal(false);
    } finally {
      setIsLoadingContent(false);
    }
  };

  const handleManualMemberLookup = async () => {
    if (!selectedFile) {
      return;
    }

    const trimmedMemberId = manualMemberId.trim();
    if (!trimmedMemberId) {
      toast.error('Please enter a member ID');
      return;
    }

    setIsLookingUpMember(true);

    try {
      const foundMember = await lookupMemberByUniqueId(trimmedMemberId);

      if (foundMember) {
        await applyFoundMember(selectedFile, foundMember);
        toast.success(
          `Member found: ${foundMember.first_name} ${foundMember.last_name}`
        );
      } else {
        setSelectedFileMemberData(null);
        setSelectedFileAssignmentStatus(null);
        toast.error('No member found with this ID');
      }
    } catch (error) {
      console.error(`Error fetching member ${trimmedMemberId}:`, error);
      toast.error('Failed to look up member');
    } finally {
      setIsLookingUpMember(false);
    }
  };

  const handleAssignUnassignMember = async () => {
    if (!selectedFile || !selectedFileMemberData || !selectedFileAssignmentStatus) {
      toast.error('Member data not available');
      return;
    }

    // Check if date of birth matches between HL7 file and member
    if (parsedHL7?.memberInfo?.dateOfBirth && selectedFileMemberData?.dob) {
      const hl7Dob = new Date(parsedHL7.memberInfo.dateOfBirth);
      const memberDob = new Date(selectedFileMemberData.dob);
      
      // Compare dates (ignoring time)
      if (hl7Dob.getFullYear() !== memberDob.getFullYear() ||
        hl7Dob.getMonth() !== memberDob.getMonth() ||
        hl7Dob.getDate() !== memberDob.getDate()) {
        const hl7DobFormatted = hl7Dob.toLocaleDateString();
        const memberDobFormatted = memberDob.toLocaleDateString();
        toast.error(`Date of birth mismatch: HL7 file DOB (${hl7DobFormatted}) does not match member DOB (${memberDobFormatted}). Cannot assign report to member.`);
        return;
      }
    }

    setIsLoadingAssignment(true);

    try {
      if (selectedFileAssignmentStatus.isAssigned) {
        // Unassign file from member
        // Get the file ID from the member's assigned files
        const memberFiles = await listMemberInuviFiles(selectedFileAssignmentStatus.memberId!);
        const assignedFile = memberFiles.files.find((memberFile: any) => memberFile.file_key === selectedFile.key);
        
        if (!assignedFile) {
          throw new Error('File not found in member\'s assigned files');
        }
        
        await unassignFileFromMember(assignedFile.id);
        toast.success(`File "${selectedFile.key}" has been unassigned from member "${selectedFileAssignmentStatus.memberName}"`);
        
        setSelectedFileAssignmentStatus(prev => prev ? {
          ...prev,
          isAssigned: false
        } : null);
      } else {
        // Assign file to member
        await assignInuviFileToMember(selectedFileAssignmentStatus.memberId!, selectedFile.key);
        toast.success(`File "${selectedFile.key}" has been assigned to member "${selectedFileAssignmentStatus.memberName}"`);
        
        setSelectedFileAssignmentStatus(prev => prev ? {
          ...prev,
          isAssigned: true
        } : null);
      }
    } catch (error) {
      console.error('Error assigning/unassigning file to member:', error);
      toast.error(`Failed to ${selectedFileAssignmentStatus.isAssigned ? 'unassign' : 'assign'} file to member`);
    } finally {
      setIsLoadingAssignment(false);
    }
  };

  const handleViewNotes = (notes: string[]) => {
    setSelectedNotes(notes);
    setShowNotesModal(true);
  };

  if (loading && files.length === 0) {
    return <Loader />;
  }

  return (
    <div className='inuvi-files-page page-container'>
      <div className='inuvi-files-header'>
        <h2 className='inuvi-files-title'>Inuvi Files ({totalCount})</h2>
      </div>
      <div className='scroll-container'>
        <div className='table-container inuvi-files-table'>
          <div className='table-body' ref={scrollContainerRef}>
          {files?.length === 0 ? (
            <div className='empty-message'>No files found</div>
          ) : (
            files?.map((file) => (
              <div key={file.key} className='table-row'>
                <div className='file-name'>{file.key}</div>
                <div className='member-name'>{fileMemberNames[file.key] || 'Loading...'}</div>
                <div className='upload-date'>
                  {new Date(file.last_modified).toLocaleDateString()}
                </div>
                <div className='actions'>
                  <button 
                    onClick={() => handleViewClick(file)}
                    className="view-button"
                  >
                    <img src={viewIcon} alt="View" />
                  </button>
                  <a href={file.url} target="_blank" rel="noopener noreferrer" >
                    <img src={downloadIcon} alt="Download" />
                  </a>
                </div>
              </div>
            ))
          )}
          {isLoadingMore && <Loader />}
          </div>
        </div>
      </div>

      <Modal 
        isOpen={showViewModal} 
        onClose={() => {
          setShowViewModal(false);
          setManualMemberId('');
        }}
        width="970px"
      >
        <div className="hl7-viewer-modal">
          <h2>HL7 File Content</h2>
          {isLoadingContent ? (
            <div>Loading content...</div>
          ) : parsedHL7 ? (
            <>
              <div className="member-info">
                <h3>Member Information</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <span className="label">ID:</span>
                    <span className="value">{parsedHL7.memberInfo.id}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Name:</span>
                    <span className="value">{parsedHL7.memberInfo.name}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Date of Birth:</span>
                    <span className="value">{parsedHL7.memberInfo.dateOfBirth}</span>
                  </div>
                  <div className="info-item">
                    <span className="label">Gender:</span>
                    <span className="value">{parsedHL7.memberInfo.gender}</span>
                  </div>
                  <div className="info-item">
                    <span className="label"> Sample Received:</span>
                    <span className="value">{parsedHL7.memberInfo.dateSampleReceived}</span>
                  </div>
                </div>
              </div>

              <div className="test-results">
                <h3>Test Results</h3>
                <table>
                  <thead>
                    <tr>
                      <th>Test Name</th>
                      <th>Value</th>
                      <th>Unit</th>
                      <th>Reference Range</th>
                      <th>Status</th>
                      <th>Notes</th>
                    </tr>
                  </thead>
                  <tbody>
                    {parsedHL7.testResults.map((result: any, index: number) => (
                      <tr key={index}>
                        <td>{result.testName}</td>
                        <td>{result.value}</td>
                        <td>{result.unit}</td>
                        <td>{result.referenceRange}</td>
                        <td>{result.status}</td>
                        <td>
                          {result.notes.length > 0 && 
                            <button className='view-notes-button' onClick={() => handleViewNotes(result.notes)}><img src={viewIcon} alt="View" /></button>
                          }
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {selectedFileMemberData && selectedFileAssignmentStatus && (
                <div className="assign-section">
                  <h3>Member Assignment</h3>
                  <div className="assignment-info">
                    <p>
                      <strong>Member:</strong> {selectedFileAssignmentStatus.memberName}
                    </p>
                    <p>
                      <strong>Status:</strong> 
                      <span className={selectedFileAssignmentStatus.isAssigned ? 'assigned' : 'unassigned'}>
                        {selectedFileAssignmentStatus.isAssigned ? 'Assigned' : 'Not Assigned'}
                      </span>
                    </p>
                  </div>
                  <div className="assignment-actions">
                    <div className={selectedFileAssignmentStatus.isAssigned ? 'unassign-button' : ''}>
                      <Button
                        text={selectedFileAssignmentStatus.isAssigned ? `Unassign Member - ${selectedFileAssignmentStatus.memberName} (ID: ${selectedFileMemberData.unique_id})` : `Assign Member - ${selectedFileAssignmentStatus.memberName} (ID: ${selectedFileMemberData.unique_id})`}
                        onClick={handleAssignUnassignMember}
                        isDisabled={isLoadingAssignment}
                      />
                    </div>
                  </div>
                </div>
              )}

              {!selectedFileMemberData && (
                <div className="assign-section">
                  <h3>Member Assignment</h3>
                  <div className="assignment-info">
                    {parsedHL7?.memberInfo?.id ? (
                      <>
                        <p className="no-member-message">
                          <strong>No member found to assign</strong>
                        </p>
                        <p>
                          Member ID from file: <span className="member-id">{parsedHL7.memberInfo.id}</span>
                        </p>
                        <p>
                          Member name from file: <span className="member-name">{parsedHL7.memberInfo.name || '—'}</span>
                        </p>
                      </>
                    ) : (
                      <>
                        <p className="no-member-message">
                          <strong>Member ID not found in file</strong>
                        </p>
                        <p>
                          Member name from file: <span className="member-name">{parsedHL7.memberInfo.name || '—'}</span>
                        </p>
                      </>
                    )}
                    {parsedHL7.memberInfo.dateOfBirth && (
                      <p>
                        Date of birth from file: <span className="member-dob">{parsedHL7.memberInfo.dateOfBirth}</span>
                      </p>
                    )}
                    <p>Enter the member ID below to look up and assign this file.</p>
                  </div>
                  <div className="manual-member-id-form">
                    <TextInput
                      name="manualMemberId"
                      title="Member ID"
                      value={manualMemberId}
                      onChange={(e) => setManualMemberId(e.target.value)}
                      placeholder="Enter member unique ID"
                      isDisabled={isLookingUpMember}
                    />
                    <Button
                      text={isLookingUpMember ? 'Looking up...' : 'Find Member'}
                      onClick={handleManualMemberLookup}
                      isDisabled={isLookingUpMember || !manualMemberId.trim()}
                    />
                  </div>
                </div>
              )}
            </>
          ) : (
            <div>Error loading file content</div>
          )}
        </div>
      </Modal>

      <Modal
        isOpen={showNotesModal}
        onClose={() => setShowNotesModal(false)}
        width="600px"
      >
        <div className="notes-modal">
          <h3>Test Notes</h3>
          <div className="notes-content">
            {selectedNotes.map((note: string, index: number) => (
              <div key={index} className="note-item">
                {note}
              </div>
            ))}
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default InuviFiles;
